import { z } from 'zod';

// ─── Helper Enums / Unions ──────────────────────────────────────────────────

export const SeveritySchema = z.enum(['low', 'medium', 'high', 'critical']).catch('medium');
export type Severity = z.infer<typeof SeveritySchema>;

export const ObjectionCategorySchema = z
  .enum([
    'efficacy_doubt',
    'safety_concern',
    'access_barrier',
    'guideline_conflict',
    'experience_conflict',
    'patient_fit',
    'competitive_preference',
    'data_insufficiency',
    'workflow_burden',
    'other',
  ])
  .catch('other');
export type ObjectionCategory = z.infer<typeof ObjectionCategorySchema>;

export const RootCauseSchema = z
  .enum([
    'clinical_experience',
    'published_evidence',
    'peer_influence',
    'patient_population',
    'formulary_constraints',
    'habit',
    'risk_aversion',
    'other',
  ])
  .catch('other');
export type RootCause = z.infer<typeof RootCauseSchema>;

export const EvidenceTypeSchema = z
  .enum([
    'rct',
    'real_world',
    'meta_analysis',
    'case_study',
    'expert_opinion',
    'guideline',
    'mechanistic',
    'patient_reported',
  ])
  .catch('expert_opinion');
export type EvidenceType = z.infer<typeof EvidenceTypeSchema>;

export const ToneSchema = z
  .enum(['authoritative', 'conversational', 'empathetic', 'clinical', 'urgent', 'neutral', 'aspirational'])
  .catch('neutral');
export type Tone = z.infer<typeof ToneSchema>;

export const BehaviorHorizonSchema = z
  .enum(['immediate', 'within_week', 'within_month', 'within_quarter', 'unlikely'])
  .catch('within_month');
export type BehaviorHorizon = z.infer<typeof BehaviorHorizonSchema>;

// ─── Evidence & Score Primitives ────────────────────────────────────────────

export const EvidenceSpanSchema = z.object({
  source: z.enum(['message', 'reaction', 'personaProfile']).catch('message'),
  quote: z.string().catch(''),
  impact: z.enum(['positive', 'negative', 'neutral']).catch('neutral'),
});
export type EvidenceSpan = z.infer<typeof EvidenceSpanSchema>;

export const ScoreSchema = z.object({
  value: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidence: z.array(EvidenceSpanSchema).catch([]),
  personaAttributesUsed: z.array(z.string()).catch([]),
});
export type Score = z.infer<typeof ScoreSchema>;

// ─── Input Schemas ──────────────────────────────────────────────────────────

export const MessageTestingMessageSchema = z.object({
  id: z.string(),
  code: z.string().optional().default(''),
  value: z.string(),
  category: z.string(),
});
export type MessageTestingMessage = z.infer<typeof MessageTestingMessageSchema>;

export const MessageTestingRequestSchema = z.object({
  segmentIdentifiers: z.array(z.string()).min(1),
  messages: z.array(MessageTestingMessageSchema).min(1),
  brand: z.string(),
  objective: z.string().optional(),
});
export type MessageTestingRequest = z.infer<typeof MessageTestingRequestSchema>;

// ─── Step 1: Reaction Transcript ────────────────────────────────────────────

export const ReactionArcPhaseSchema = z.object({
  phase: z.enum(['opener', 'middle', 'close']).catch('middle'),
  sentiment: z
    .enum(['positive', 'neutral', 'negative', 'mixed', 'measured', 'skeptical', 'cautious'])
    .catch('neutral'),
  summary: z.string().catch(''),
});

export const ReactionTranscriptSchema = z.object({
  messageId: z.string(),
  segmentIdentifier: z.string(),
  transcript: z.string(),
  personaAttributesInvoked: z.array(z.string()).catch([]),
  reactionArc: z.array(ReactionArcPhaseSchema).catch([]),
});
export type ReactionTranscript = z.infer<typeof ReactionTranscriptSchema>;

// ─── Step 2: Message Testing Cell ───────────────────────────────────────────

export const ObjectionSchema = z.object({
  text: z.string(),
  category: ObjectionCategorySchema,
  severity: SeveritySchema,
  addressableLive: z.boolean().catch(false),
  rootCause: RootCauseSchema,
  suggestedRebuttal: z.string().catch(''),
  personaAttribute: z.string().catch(''),
  evidenceSpan: EvidenceSpanSchema.optional(),
  fingerprint: z.string().catch(''),
});

export const MissingHookSchema = z.object({
  expectedElement: z.string(),
  whyExpected: z.string(),
  personaAttribute: z.string().catch(''),
  predictedImpact: z.enum(['low', 'medium', 'high']).catch('medium'),
  priorityRank: z.number().catch(0),
});

export const TriggerPhraseSchema = z.object({
  phrase: z.string(),
  why: z.string(),
  personaAttribute: z.string().catch(''),
});

export const WastedPhraseSchema = z.object({
  phrase: z.string(),
  why: z.string(),
});

export const TriggerLanguageSchema = z.object({
  positive: z.array(TriggerPhraseSchema).catch([]),
  negative: z.array(TriggerPhraseSchema).catch([]),
  wasted: z.array(WastedPhraseSchema).catch([]),
  missingVocabulary: z.array(z.string()).catch([]),
});

export const AttentionFlowSchema = z.object({
  hookStrength: ScoreSchema,
  dropOffPoint: z.string().nullable(),
  reEngagementMoments: z.array(z.string()).catch([]),
  cognitiveLoadFlags: z.array(z.string()).catch([]),
});

export const CredibilityErosionMomentSchema = z.object({
  quote: z.string(),
  reason: z.string(),
});

export const TrustSignalsSchema = z.object({
  evidenceTypeMatch: z.boolean().catch(false),
  evidenceTypesPresent: z.array(EvidenceTypeSchema).catch([]),
  evidenceTypesPreferred: z.array(EvidenceTypeSchema).catch([]),
  credibilityErosionMoments: z.array(CredibilityErosionMomentSchema).catch([]),
});

export const ToneFitDetailSchema = z.object({
  detectedTone: ToneSchema,
  preferredTone: ToneSchema,
  perceivedRepPosture: z.enum(['peer', 'vendor', 'educator', 'pest']).catch('educator'),
  patientCentricity: ScoreSchema,
});

export const BehavioralPredictionsSchema = z.object({
  immediateReaction: z
    .enum(['engage', 'politely_dismiss', 'redirect', 'clarifying_question'])
    .catch('engage'),
  likelyFollowupQuestion: z.string().catch(''),
  infoTheyWouldVerify: z.array(z.string()).catch([]),
  wordOfMouthProbability: ScoreSchema,
  behaviorChangeHorizon: BehaviorHorizonSchema,
});

export const CompetitiveFramingSchema = z.object({
  implicitComparator: z.string().nullable(),
  switchingCostPerception: z.string().catch(''),
  currentStateAcknowledgment: z.boolean().catch(false),
});

export const CategoryFitDetailSchema = z.object({
  categoryPriorityForPersona: ScoreSchema,
  suggestedBetterCategory: z.string().nullable(),
  categoryExpectationGaps: z.array(z.string()).catch([]),
});

export const AttributeConflictSchema = z.object({
  a: z.string(),
  b: z.string(),
  resolution: z.string(),
});

export const AttributeUtilizationSchema = z.object({
  invoked: z.array(z.string()).catch([]),
  unused: z.array(z.string()).catch([]),
  conflicts: z.array(AttributeConflictSchema).catch([]),
});

export const ComplianceFlagSchema = z.object({
  type: z
    .enum(['off_label_hint', 'unbalanced_claim', 'missing_isi_cue', 'unqualified_superlative', 'other'])
    .catch('other'),
  severity: SeveritySchema,
  quote: z.string(),
  rationale: z.string(),
});

export const ImprovementSuggestionSchema = z.object({
  change: z.string(),
  rationale: z.string(),
  predictedScoreLift: z.enum(['low', 'medium', 'high']).catch('medium'),
  scoresAffected: z.array(z.string()).catch([]),
});

export const OverallAssessmentSchema = z.object({
  verdict: z.enum(['strong_fit', 'workable', 'weak_fit', 'do_not_use']).catch('workable'),
  oneLineSummary: z.string(),
  topStrength: z.string().catch(''),
  topWeakness: z.string().catch(''),
});

export const VerdictSchema = OverallAssessmentSchema.shape.verdict;
export type Verdict = z.infer<typeof VerdictSchema>;

export const MessageTestingScoresSchema = z.object({
  personaResonance: ScoreSchema,
  objectiveAlignment: ScoreSchema,
  categoryFit: ScoreSchema,
  predictedEngagement: ScoreSchema,
  predictedActionLikelihood: ScoreSchema,
  clarity: ScoreSchema,
  credibility: ScoreSchema,
  cognitiveLoad: ScoreSchema,
  differentiation: ScoreSchema,
  ctaStrength: ScoreSchema,
  toneFit: ScoreSchema,
});

// ─── Sub-Judge Output Schemas ───────────────────────────────────────────────

export const ScoresJudgeOutputSchema = z.object({
  scores: MessageTestingScoresSchema,
  attentionFlow: AttentionFlowSchema,
  trustSignals: TrustSignalsSchema,
  toneFitDetail: ToneFitDetailSchema,
  categoryFitDetail: CategoryFitDetailSchema,
  attributeUtilization: AttributeUtilizationSchema,
  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type ScoresJudgeOutput = z.infer<typeof ScoresJudgeOutputSchema>;

export const ObjectionsJudgeOutputSchema = z.object({
  objections: z.array(ObjectionSchema),
  missingHooks: z.array(MissingHookSchema),
  competitiveFraming: CompetitiveFramingSchema,
});
export type ObjectionsJudgeOutput = z.infer<typeof ObjectionsJudgeOutputSchema>;

export const BehaviorJudgeOutputSchema = z.object({
  behavioralPredictions: BehavioralPredictionsSchema,
});
export type BehaviorJudgeOutput = z.infer<typeof BehaviorJudgeOutputSchema>;

export const LanguageJudgeOutputSchema = z.object({
  triggerLanguage: TriggerLanguageSchema,
});
export type LanguageJudgeOutput = z.infer<typeof LanguageJudgeOutputSchema>;

// ─── Composite Cell ─────────────────────────────────────────────────────────

export const MessageTestingCellSchema = z.object({
  messageId: z.string(),
  segmentId: z.string(),
  generatedAt: z.string(),

  scores: MessageTestingScoresSchema,

  objections: z.array(ObjectionSchema),
  missingHooks: z.array(MissingHookSchema),
  triggerLanguage: TriggerLanguageSchema,
  attentionFlow: AttentionFlowSchema,
  trustSignals: TrustSignalsSchema,
  toneFitDetail: ToneFitDetailSchema,
  behavioralPredictions: BehavioralPredictionsSchema,
  competitiveFraming: CompetitiveFramingSchema,
  categoryFitDetail: CategoryFitDetailSchema,
  attributeUtilization: AttributeUtilizationSchema,
  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type MessageTestingCell = z.infer<typeof MessageTestingCellSchema>;

// ─── Rollup Schemas ─────────────────────────────────────────────────────────

const RollupRecommendationSchema = z.object({
  action: z.string(),
  rationale: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
});

const RollupMessageReferenceSchema = z.object({
  messageId: z.string(),
  why: z.string(),
});

export const SegmentRollupSchema = z.object({
  segmentIdentifier: z.string(),
  segmentName: z.string(),
  executiveSummary: z.string(),
  keyStrengths: z.array(z.string()),
  keyWeaknesses: z.array(z.string()),
  topObjectionPatterns: z.array(
    z.object({
      pattern: z.string(),
      frequency: z.number(),
      suggestedRebuttal: z.string(),
    })
  ),
  categoryPerformanceRanking: z.array(
    z.object({
      category: z.string(),
      meanComposite: z.number(),
      verdict: VerdictSchema,
    })
  ),
  attributeUtilizationInsights: z.string(),
  recommendations: z.array(RollupRecommendationSchema),
});
export type SegmentRollup = z.infer<typeof SegmentRollupSchema>;

export const CategoryRollupSchema = z.object({
  category: z.string(),
  executiveSummary: z.string(),
  crossPersonaAnalysis: z.string(),
  bestMessages: z.array(RollupMessageReferenceSchema),
  worstMessages: z.array(RollupMessageReferenceSchema),
  personaInsights: z.array(
    z.object({
      segmentIdentifier: z.string(),
      segmentName: z.string(),
      fitAssessment: z.string(),
      keyObjection: z.string().nullable(),
    })
  ),
  recommendations: z.array(RollupRecommendationSchema),
});
export type CategoryRollup = z.infer<typeof CategoryRollupSchema>;

export const MatrixRollupSchema = z.object({
  executiveSummary: z.string(),
  portfolioHealthScore: z.number().min(0).max(100),
  universalWinners: z.array(RollupMessageReferenceSchema),
  universalLosers: z.array(RollupMessageReferenceSchema),
  crossSegmentPatterns: z.array(z.string()),
  crossCategoryPatterns: z.array(z.string()),
  strategicRecommendations: z.array(
    RollupRecommendationSchema.extend({
      impactEstimate: z.string(),
    })
  ),
  priorityActionItems: z.array(
    z.object({
      action: z.string(),
      urgency: z.enum(['immediate', 'short_term', 'long_term']),
    })
  ),
});
export type MatrixRollup = z.infer<typeof MatrixRollupSchema>;

export const MessageTestingRollupsSchema = z.object({
  segmentRollups: z.array(SegmentRollupSchema),
  categoryRollups: z.array(CategoryRollupSchema),
  matrixRollup: MatrixRollupSchema,
});
export type MessageTestingRollups = z.infer<typeof MessageTestingRollupsSchema>;

// ─── Result Envelope ────────────────────────────────────────────────────────

export const MessageTestingProgressSchema = z.object({
  runIdentifier: z.string(),
  completed: z.number(),
  total: z.number(),
  latestCell: MessageTestingCellSchema.optional(),
});
export type MessageTestingProgress = z.infer<typeof MessageTestingProgressSchema>;

export const MessageTestingResultSchema = z.object({
  runIdentifier: z.string(),
  status: z.enum(['processing', 'analyzing', 'completed', 'failed']),
  matrix: z.array(MessageTestingCellSchema),
  progress: z.object({
    completed: z.number(),
    total: z.number(),
  }),
  rollups: MessageTestingRollupsSchema.optional(),
});
export type MessageTestingResult = z.infer<typeof MessageTestingResultSchema>;

// ─── Message Testing State ──────────────────────────────────────────────────

export const MessageTestingPhaseSchema = z.enum(['idle', 'configuring', 'running', 'completed', 'error']);
export type MessageTestingPhase = z.infer<typeof MessageTestingPhaseSchema>;

export const MessageTestingStateSchema = z.object({
  phase: MessageTestingPhaseSchema.default('idle'),
  /** Messages to test — typically sourced from message generation output. */
  messages: z.array(MessageTestingMessageSchema).default([]),
  /** Segment identifiers whose personas will be used for simulation. */
  segmentIdentifiers: z.array(z.string()).default([]),
  /** Brand context for the testing simulation. */
  brand: z.string().default('Elderberry'),
  /** Optional objective for signal extraction scoring. */
  objective: z.string().nullable().default(null),
  /** Identifier of the current / most recent BullMQ run. */
  runIdentifier: z.string().nullable().default(null),
  /** Accumulated result cells from the testing matrix. */
  results: z.array(MessageTestingCellSchema).default([]),
  /** Live progress tracking. */
  progress: z
    .object({
      completed: z.number(),
      total: z.number(),
    })
    .nullable()
    .default(null),
  /** Error message if the testing run failed. */
  error: z.string().nullable().default(null),
});
export type MessageTestingState = z.infer<typeof MessageTestingStateSchema>;

/** Default initial state for message testing. */
export function getDefaultMessageTestingState(): MessageTestingState {
  return MessageTestingStateSchema.parse({});
}

// ─── Message Testing Actions ────────────────────────────────────────────────

export const MessageTestingActionType = {
  SetTestingDetails: 'SET_TESTING_DETAILS',
  StartTesting: 'START_TESTING',
  SetTestingProgress: 'SET_TESTING_PROGRESS',
  SetTestingResults: 'SET_TESTING_RESULTS',
  SetTestingError: 'SET_TESTING_ERROR',
  ResetTesting: 'RESET_TESTING',
} as const;

export type MessageTestingActionType =
  (typeof MessageTestingActionType)[keyof typeof MessageTestingActionType];

export type MessageTestingAction =
  | {
      type: typeof MessageTestingActionType.SetTestingDetails;
      payload: {
        messages?: MessageTestingMessage[];
        segmentIdentifiers?: string[];
        brand?: string;
        objective?: string | null;
      };
    }
  | {
      type: typeof MessageTestingActionType.StartTesting;
      payload: {
        runIdentifier: string;
        totalPairs: number;
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingProgress;
      payload: {
        completed: number;
        total: number;
        latestCell?: MessageTestingCell;
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingResults;
      payload: {
        matrix: MessageTestingCell[];
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingError;
      payload: { error: string };
    }
  | {
      type: typeof MessageTestingActionType.ResetTesting;
    };

// ─── Message Testing Reducer ────────────────────────────────────────────────

/**
 * Pure reducer for MessageTestingState.
 * Single source of truth for all testing state mutations — used by both
 * backend (session persistence) and frontend (optimistic updates).
 */
export function messageTestingReducer(
  state: MessageTestingState,
  action: MessageTestingAction
): MessageTestingState {
  switch (action.type) {
    case MessageTestingActionType.SetTestingDetails: {
      const { messages, segmentIdentifiers, brand, objective } = action.payload;
      const next = { ...state };
      if (messages !== undefined) next.messages = messages;
      if (segmentIdentifiers !== undefined) next.segmentIdentifiers = segmentIdentifiers;
      if (brand !== undefined) next.brand = brand;
      if (objective !== undefined) next.objective = objective;
      if (next.phase === 'idle') next.phase = 'configuring';
      return next;
    }

    case MessageTestingActionType.StartTesting:
      return {
        ...state,
        phase: 'running',
        runIdentifier: action.payload.runIdentifier,
        progress: { completed: 0, total: action.payload.totalPairs },
        results: [],
        error: null,
      };

    case MessageTestingActionType.SetTestingProgress: {
      const { completed, total, latestCell } = action.payload;
      return {
        ...state,
        progress: { completed, total },
        results: latestCell ? [...state.results, latestCell] : state.results,
      };
    }

    case MessageTestingActionType.SetTestingResults:
      return {
        ...state,
        phase: 'completed',
        results: action.payload.matrix,
        progress: state.progress
          ? { ...state.progress, completed: action.payload.matrix.length }
          : { completed: action.payload.matrix.length, total: action.payload.matrix.length },
      };

    case MessageTestingActionType.SetTestingError:
      return {
        ...state,
        phase: 'error',
        error: action.payload.error,
      };

    case MessageTestingActionType.ResetTesting:
      return getDefaultMessageTestingState();

    default:
      return state;
  }
}

/**
 * Apply a sequence of testing actions to a state, returning the final state.
 */
export function applyMessageTestingActions(
  state: MessageTestingState,
  actions: MessageTestingAction[]
): MessageTestingState {
  return actions.reduce(messageTestingReducer, state);
}
